import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
  Alert,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { useEquipmentStore } from '../../../stores/equipmentStore';
import { useDashboardStore } from '../../../stores/dashboardStore';
import { useTranslation } from '../../../i18n';
import { BRAND_COLORS, alpha } from '../../../styles/theme';
import {
  OperationsEquipment,
  OperationsEquipmentCreate,
  OperationsEquipmentUpdate,
} from '../../../types/api';

// ─── Equipment form dialog ───

interface EquipmentFormState {
  name: string;
  location: string;
  quantity: string;
  drone_serial: string;
  photo_url: string;
  notes: string;
}

const emptyForm: EquipmentFormState = {
  name: '',
  location: '',
  quantity: '1',
  drone_serial: '',
  photo_url: '',
  notes: '',
};

function formFromEquipment(eq: OperationsEquipment): EquipmentFormState {
  return {
    name: eq.name,
    location: eq.location ?? '',
    quantity: String(eq.quantity),
    drone_serial: eq.drone_serial ?? '',
    photo_url: eq.photo_url ?? '',
    notes: eq.notes ?? '',
  };
}

const OperationsEquipmentSection: React.FC = () => {
  const { t } = useTranslation();
  const droneSerials = useDashboardStore((s) => s.data.serialNumbers);
  const {
    equipment,
    loading,
    error,
    loadEquipment,
    createEquipment,
    updateEquipment,
    deleteEquipment,
  } = useEquipmentStore();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EquipmentFormState>(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<OperationsEquipment | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadEquipment();
  }, [loadEquipment]);

  const handleOpenCreate = useCallback(() => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  }, []);

  const handleOpenEdit = useCallback((eq: OperationsEquipment) => {
    setEditingId(eq.id);
    setForm(formFromEquipment(eq));
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingId(null);
    setForm(emptyForm);
  }, []);

  const handleSave = useCallback(async () => {
    const qty = parseInt(form.quantity, 10);
    if (!form.name.trim()) return;
    if (isNaN(qty) || qty < 1) return;

    setSaving(true);
    try {
      let result;
      if (editingId) {
        const data: OperationsEquipmentUpdate = {
          name: form.name.trim(),
          location: form.location.trim() || null,
          quantity: qty,
          drone_serial: form.drone_serial.trim() || null,
          photo_url: form.photo_url.trim() || null,
          notes: form.notes.trim() || null,
        };
        result = await updateEquipment(editingId, data);
      } else {
        const data: OperationsEquipmentCreate = {
          name: form.name.trim(),
          location: form.location.trim() || undefined,
          quantity: qty,
          drone_serial: form.drone_serial.trim() || undefined,
          photo_url: form.photo_url.trim() || undefined,
          notes: form.notes.trim() || undefined,
        };
        result = await createEquipment(data);
      }
      if (result) {
        handleCloseDialog();
      }
    } finally {
      setSaving(false);
    }
  }, [form, editingId, createEquipment, updateEquipment, handleCloseDialog]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteConfirm) return;
    const success = await deleteEquipment(deleteConfirm.id);
    if (success) {
      setDeleteConfirm(null);
    }
  }, [deleteConfirm, deleteEquipment]);

  const isFormValid = form.name.trim().length > 0 && parseInt(form.quantity, 10) >= 1;

  return (
    <Box sx={{ mt: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BuildIcon sx={{ color: BRAND_COLORS.forestGreen.main }} />
          <Typography variant="h6">{t('operationsEquipment.title')}</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={handleOpenCreate}>
          {t('operationsEquipment.add')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && equipment.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : equipment.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {t('operationsEquipment.noEquipment')}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.04) }}>
                <TableCell>{t('operationsEquipment.name')}</TableCell>
                <TableCell>{t('operationsEquipment.location')}</TableCell>
                <TableCell align="center">{t('operationsEquipment.quantity')}</TableCell>
                <TableCell>{t('operationsEquipment.drone')}</TableCell>
                <TableCell>{t('operationsEquipment.photo')}</TableCell>
                <TableCell align="right">{t('operationsEquipment.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {equipment.map((eq) => (
                <TableRow key={eq.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {eq.name}
                    </Typography>
                    {eq.notes && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {eq.notes.length > 80 ? `${eq.notes.slice(0, 80)}...` : eq.notes}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{eq.location ?? '\u2014'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{eq.quantity}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {eq.drone_serial ? `SN${eq.drone_serial}` : '\u2014'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {eq.photo_url ? (
                      <Avatar variant="rounded" src={eq.photo_url} sx={{ width: 36, height: 36 }} />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {'\u2014'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('operationsEquipment.edit')}>
                      <IconButton size="small" onClick={() => handleOpenEdit(eq)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('operationsEquipment.delete')}>
                      <IconButton size="small" color="error" onClick={() => setDeleteConfirm(eq)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? t('operationsEquipment.editTitle') : t('operationsEquipment.addTitle')}
        </DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}
        >
          <TextField
            label={t('operationsEquipment.name')}
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
            fullWidth
            size="small"
            inputProps={{ maxLength: 200 }}
          />
          <TextField
            label={t('operationsEquipment.location')}
            value={form.location}
            onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
            fullWidth
            size="small"
            inputProps={{ maxLength: 500 }}
          />
          <TextField
            label={t('operationsEquipment.quantity')}
            type="number"
            value={form.quantity}
            onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
            fullWidth
            size="small"
            inputProps={{ min: 1 }}
          />
          <TextField
            label={t('operationsEquipment.drone')}
            value={form.drone_serial}
            onChange={(e) => setForm((f) => ({ ...f, drone_serial: e.target.value }))}
            fullWidth
            size="small"
            placeholder={droneSerials.length > 0 ? `e.g. ${droneSerials[0]}` : ''}
          />
          <TextField
            label={t('operationsEquipment.photoUrl')}
            value={form.photo_url}
            onChange={(e) => setForm((f) => ({ ...f, photo_url: e.target.value }))}
            fullWidth
            size="small"
          />
          <TextField
            label={t('operationsEquipment.notes')}
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            fullWidth
            size="small"
            multiline
            minRows={2}
            maxRows={4}
            inputProps={{ maxLength: 5000 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSave} variant="contained" disabled={!isFormValid || saving}>
            {saving ? <CircularProgress size={20} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogTitle>{t('operationsEquipment.delete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('operationsEquipment.confirmDelete')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>{t('common.cancel')}</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OperationsEquipmentSection;
